import React, { useState, useEffect } from 'react';
import { useSettings } from '../context/SettingsContext';

export default function ContactPage() {
    const settings = useSettings();
    const [time, setTime] = useState('');
    const [form, setForm] = useState({ name: '', email: '', service: 'Product Strategy', message: '' });
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        const tick = () => {
            const d = new Date();
            setTime(d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', timeZoneName: 'short' }));
        };
        tick();
        const id = setInterval(tick, 1000);
        return () => clearInterval(id);
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        setError('');

        try {
            // 1. Send Email via WordPress REST API
            const response = await fetch('/index.php?rest_route=/digital-services/v1/contact', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(form),
            });

            const data = await response.json();

            if (response.ok && data.success) {
                // 2. Open WhatsApp Redirect
                if (settings.phone) {
                    const cleanPhone = settings.phone.replace(/[^0-9]/g, '');
                    const whatsappMsg = encodeURIComponent(`Hello! My name is ${form.name}.\n\nService: ${form.service}\nMessage: ${form.message}`);
                    const whatsappUrl = `https://wa.me/${cleanPhone}?text=${whatsappMsg}`;
                    window.open(whatsappUrl, '_blank');
                }

                setSubmitted(true);
            } else {
                setError(data.message || 'Fatal error during transmission. Please try again or contact us directly.');
            }
        } catch (err) {
            console.error('Submission error:', err);
            setError('Connection failed. Please check your network and try again.');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="pt-16 min-h-screen">
            {/* Header */}
            <section className="py-20 px-6 sm:px-8 relative overflow-hidden">
                <div className="absolute inset-0 bg-hero-glow pointer-events-none" />
                <div className="max-w-6xl mx-auto relative z-10">
                    <p className="section-tag">LET'S SYNTHESIZE</p>
                    <h1 className="text-5xl md:text-6xl font-black text-nocturne-text leading-tight mb-6 uppercase">
                        Architecting the <span className="gradient-text">Digital</span><br />
                        <span className="gradient-text">Future.</span>
                    </h1>
                    <p className="text-nocturne-muted text-lg max-w-lg leading-relaxed">
                        Whether you're looking for a bespoke digital strategy or a high-performance interface, our doors (and pixels) are always open.
                    </p>
                </div>
            </section>

            {/* Form + Info */}
            <section className="py-12 px-6 sm:px-8 pb-24">
                <div className="max-w-6xl mx-auto grid md:grid-cols-[1.2fr_1fr] gap-8">
                    {/* Contact form */}
                    <div className="card border-nocturne-purple/20">
                        {submitted ? (
                            <div className="flex flex-col items-center justify-center py-20 text-center animate-fade-in">
                                <div className="text-6xl mb-6">🚀</div>
                                <h3 className="text-3xl font-black text-nocturne-text mb-2 uppercase">Request Transmitted!</h3>
                                <p className="text-nocturne-muted max-w-sm">
                                    We've received your coordinates. Check your email for confirmation, and we've also opened a WhatsApp chat for you.
                                </p>
                            </div>
                        ) : (
                            <form onSubmit={handleSubmit} className="space-y-6">
                                {error && (
                                    <div className="bg-red-500/10 border border-red-500/50 text-red-500 p-4 rounded-xl text-sm mb-6">
                                        ⚠️ {error}
                                    </div>
                                )}

                                <div className="grid sm:grid-cols-2 gap-6">
                                    <div>
                                        <label className="block text-nocturne-purple text-xs font-bold uppercase tracking-wider mb-2">Full Name</label>
                                        <input
                                            className="input-field"
                                            placeholder="John Doe"
                                            value={form.name}
                                            onChange={e => setForm({ ...form, name: e.target.value })}
                                            disabled={submitting}
                                            required
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-nocturne-purple text-xs font-bold uppercase tracking-wider mb-2">Email Address</label>
                                        <input
                                            className="input-field"
                                            type="email"
                                            placeholder="john@example.com"
                                            value={form.email}
                                            onChange={e => setForm({ ...form, email: e.target.value })}
                                            disabled={submitting}
                                            required
                                        />
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-nocturne-purple text-xs font-bold uppercase tracking-wider mb-2">Service Interest</label>
                                    <div className="relative">
                                        <select
                                            className="input-field appearance-none pr-8 cursor-pointer"
                                            value={form.service}
                                            onChange={e => setForm({ ...form, service: e.target.value })}
                                            disabled={submitting}
                                        >
                                            {['Product Strategy', 'Web Development', 'Mobile Apps', 'SEO & Data', 'Marketing', 'Social Media', 'Other'].map(o => (
                                                <option key={o} value={o}>{o}</option>
                                            ))}
                                        </select>
                                        <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-nocturne-muted text-xs">▼</span>
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-nocturne-purple text-xs font-bold uppercase tracking-wider mb-2">Your Vision</label>
                                    <textarea
                                        className="input-field resize-none h-32"
                                        placeholder="Tell us about your project goals..."
                                        value={form.message}
                                        onChange={e => setForm({ ...form, message: e.target.value })}
                                        disabled={submitting}
                                        required
                                    />
                                </div>

                                <button
                                    type="submit"
                                    disabled={submitting}
                                    className={`btn-primary px-10 py-4 text-base flex items-center gap-3 ${submitting ? 'opacity-70 cursor-wait' : ''}`}
                                >
                                    {submitting ? (
                                        <>
                                            <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                            Submitting...
                                        </>
                                    ) : (
                                        'Submit Request →'
                                    )}
                                </button>
                            </form>
                        )}
                    </div>

                    {/* Info panel */}
                    <div className="flex flex-col gap-6">
                        {/* Physical node card */}
                        <div className="card border-nocturne-purple/10">
                            <h3 className="text-nocturne-text font-bold mb-8 uppercase tracking-widest text-sm">Physical Node</h3>
                            <div className="space-y-6">
                                <div className="flex gap-4">
                                    <div className="w-12 h-12 shrink-0 rounded-2xl bg-nocturne-bg border border-nocturne-border flex items-center justify-center text-xl shadow-lg">📍</div>
                                    <div>
                                        <div className="text-nocturne-text font-bold text-sm mb-1">Headquarters</div>
                                        <div className="text-nocturne-muted text-xs leading-relaxed whitespace-pre-line">{settings.address}</div>
                                    </div>
                                </div>
                                <div className="flex gap-4">
                                    <div className="w-12 h-12 shrink-0 rounded-2xl bg-nocturne-bg border border-nocturne-border flex items-center justify-center text-xl shadow-lg">✉️</div>
                                    <div>
                                        <div className="text-nocturne-text font-bold text-sm mb-1">Direct Communication</div>
                                        <div className="text-nocturne-muted text-xs leading-relaxed">
                                            {settings.email}<br />
                                            {settings.phone}
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="flex gap-3 mt-10">
                                {['🌐', '🐦', '🔊'].map((icon, i) => (
                                    <button key={i} className="w-10 h-10 rounded-xl bg-nocturne-bg border border-nocturne-border flex items-center justify-center hover:border-nocturne-purple/60 hover:bg-nocturne-purple/10 transition-all text-lg shadow-md hover:shadow-nocturne-purple/10">
                                        {icon}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Map / time card */}
                        <div className="card flex-1 relative overflow-hidden min-h-[250px] border-nocturne-border/30">
                            <div className="absolute inset-0 flex items-center justify-center opacity-30">
                                <svg viewBox="0 0 200 200" className="w-full h-full">
                                    <line x1="20" y1="100" x2="180" y2="100" stroke="#8b5cf6" strokeWidth="1.5" strokeDasharray="4 4" />
                                    <line x1="100" y1="20" x2="100" y2="180" stroke="#8b5cf6" strokeWidth="1.5" strokeDasharray="4 4" />
                                    <circle cx="100" cy="100" r="4" fill="#8b5cf6" className="animate-pulse" />
                                    <circle cx="100" cy="100" r="12" stroke="#8b5cf6" strokeWidth="1" fill="none" className="animate-ping" />
                                </svg>
                            </div>
                            <div className="absolute bottom-6 left-6 bg-nocturne-bg/80 backdrop-blur-md rounded-2xl px-5 py-3 border border-nocturne-border shadow-2xl">
                                <div className="text-nocturne-purple text-[10px] font-black tracking-[0.2em] mb-1">LOCAL TIME</div>
                                <div className="text-nocturne-text font-black text-2xl tracking-tight">{time}</div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
}
